import { APIResponse, Order } from '../../../interfaces';
import { isEqual } from '@/utils/common';
import { Product } from '@/features/product/types';
import { baseSplitApi } from '@/services';


export interface Data {
  _id: Date;
  totalSales: number;
  totalOrders: number;
  totalProfit: number;
  totalPurchasedPrice: number;
  totalProductProfit?: number;
  expenseSummary?: {
    totalAmount: number;
  };
  paymentMethodTotals?: {
    UPI: number;
    Cash: number;
    Credit: number;
    Card: number;
    Other: number;
    ExchangePayment: number;
  };
}

const buildQueryString = (params: Record<string, any>): string => {
  const queryParts: string[] = [];
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParts.push(`${key}=${value}`);
    }
  });
  
  return queryParts.join('&');
};


export const orderApi = baseSplitApi.injectEndpoints({
  endpoints: builder => ({
    getOrderList: builder.query<Array<Order>, Record<string, any>>({
      query: (params) => {
        const queryString = buildQueryString(params);
        return {
          url: `/v1/order?${queryString}`,
          method: 'Get',
        };
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1 || !arg.page) {
          return newItems;
        }
        // Otherwise append for pagination
        return [...currentCache, ...newItems];
      },
      providesTags:["OrderList"],
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      
      transformErrorResponse: (
        response: { status: string | number },
      ) => response.status,
      transformResponse: (response: { data: Array<Order> }) =>
        response.data,
    }),

     getUserOrderList: builder.query<Array<Order>, Record<string, any>>({
      query: (params) => {
        const queryString = buildQueryString(params);
        return {
          url: `/v1/order?${queryString}`,
          method: 'Get',
        };
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page == 1) {
          currentCache.length = 0;
          currentCache.push(...newItems);
        } else {
          currentCache.push(...newItems);
        }
      },
      providesTags: ['OrderList'],
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: Array<Order> }) => response.data,
    }),
    getOrderCount: builder.query<number, string>({
      query: args => ({
        url: `/v1/order/count?${args}`,
        method: 'Get',
      }),
      providesTags: ['OrderCount'],
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: number }) => response?.data ?? 0,
    }),
    getOrder: builder.query<Order, Record<string, any>>({
      query: ({ orderId, ...searchParams }) => {
        const queryString = buildQueryString(searchParams);
        return {
          url: `/v1/order/${orderId}?${queryString}`,
          method: 'Get',
        };
      },
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: Order }) => response.data,
    }),
    // createOrUpdateOrder: builder.mutation<APIResponse<Order>, any>({
    //   query: body => ({
    //     url: `/v1/order`,
    //     method: 'POST',
    //     body: body,
    //   }),
    //   invalidatesTags: ['OrderCount', 'DailySales', 'LowStockCount', 'OrderList', 'ExpenseList', 'StockValue', 'ProductList'],
    // }),
    updateOrderStatus: builder.mutation<APIResponse<Order>, any>({
      query: ({ orderId, ...body }) => ({
        url: `/v1/order/` + orderId,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: ['OrderCount', 'DailySales', 'LowStockCount', 'OrderList', 'ExpenseList', 'ExpenseServiceDetails', 'StockValue', 'ProductList'],
    }),
    cancelOrder: builder.mutation<APIResponse<Order>, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/v1/order/${orderId}/cancel`,
        method: 'PATCH',
        body: {},
      }),
      invalidatesTags: ['OrderList', 'ExpenseList', 'ExpenseServiceDetails', 'OrderCount', 'DailySales', 'LowStockCount'],
    }),

    deleteOrder: builder.mutation<Order, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/v1/order/${orderId}`,
        method: 'DELETE',
        body: {},
      }),
      invalidatesTags: [
        'OrderCount',
        'DailySales',
        'LowStockCount',
        'OrderList',
        'ExpenseList',
        'StockValue',
        'ProductList',
      ],
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: Order }) => response.data,
    }),

    sellOrder: builder.mutation<APIResponse<Order>, Record<string, any>>({
      query: body => ({
        url: `/v1/order/sale`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: [
        'OrderCount',
        'DailySales',
        'LowStockCount',
        'OrderList',
        'ExpenseList',
        'StockValue',
        'ProductList',
      ],
    }),
    editOrder: builder.mutation<APIResponse<Order>, Record<string, any>>({
      query: ({orderId,...body}) => ({
        url: `/v1/order/${orderId}/edit`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: [
        'OrderCount',
        'DailySales',
        'LowStockCount',
        'OrderList',
        'ExpenseList',
        'StockValue',
        'ProductList',
      ],
    }),

    expenseOrder: builder.mutation<APIResponse<Order>, Record<string, any>>({
      query: body => ({
        url: `/v1/order/expense`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: [
        'OrderCount',
        'DailySales',
        'LowStockCount',
        'OrderList',
        'ExpenseList',
        'ExpenseOrder',
        'StockValue',
        'ProductList',
      ],
    }),
    getDailySell: builder.query<Data[], Record<string, any>>({
      query: params => ({
        url: `/v1/order/sell/daily?${params}`,
        method: 'GET',
      }),
      providesTags: ['DailySales'],
      transformErrorResponse: (response: { status: string | number }) =>
        response.status,
      transformResponse: (response: { data: Data[] }) => response.data,
    }),
    getSellerProductByBarcode: builder.query<APIResponse<Product>, Record<string, any>>({
      query: ({ barcode, ...params }) => ({
        url: `/v1/seller/store/product/barcode/${barcode}`,
        method: 'GET',
        params
      }),
    }),
    
  }),
  overrideExisting: true,
});

export const {
  useGetUserOrderListQuery,
  useGetOrderListQuery,
  useLazyGetOrderListQuery,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useUpdateOrderStatusMutation,
  useCancelOrderMutation,
  useSellOrderMutation,
  useExpenseOrderMutation,
  useDeleteOrderMutation,
  useGetOrderCountQuery,
  useGetDailySellQuery,
  useLazyGetSellerProductByBarcodeQuery,
  useEditOrderMutation
} = orderApi;
